/**
 * The assembled Belote GameModule.
 *
 * Belote is the game the engine's seams were cut for (PLAN.md §9: "treat the
 * rule-module interface as provisional until Belote is built against it"),
 * and three of them carry it:
 *
 *  - the two-phase deal: the turned card is the kitty, which everyone sees and
 *    the auction is told about (`faceUp`); after a take, `onContract` finishes
 *    the deal from the same committed permutation's stock;
 *  - anticlockwise play: seat index is turn order, so it is only
 *    `meta.rotation`, a presentation hint;
 *  - declarations: resolved as trick 1 completes (`onTrickComplete`) and laid
 *    face up in `extra`, which `visibility.revealed` owns up to.
 */
import { rankOf, suitOf } from '../../core/cards.js';
import { PARTNERSHIP_4 } from '../../core/seats.js';
import { BELOTE_TRICKS_PER_HAND, DEFAULT_BELOTE_CONFIG, validateBeloteConfig, } from './config.js';
import { beloteAuction } from './auction.js';
import { cardsDealt, resolveDeclarations } from './declarations.js';
import { beloteCurrentTrick, beloteDealPlan, beloteDeck, beloteLegalPlays, beloteRanking, beloteVisibility, } from './rules.js';
import { beloteMatchEnd, beloteScoreHand } from './scoring.js';
const INIT_EXTRA = { declarations: null, declarationsTeam: null, belote: null };
/**
 * Rules §1.4 step 4: the taker picks up the turned card, then the deal resumes
 * from the dealer's right — two more cards to the taker, three to everyone
 * else — from the stock of the permutation that was committed before the deal.
 */
export function completeClassicDeal(hand, table) {
    const taker = hand.contract.taker;
    const stock = [...hand.secret.stock];
    const holdings = hand.secret.holdings.map((h) => [...h]);
    holdings[taker].push(...hand.secret.kitty);
    for (let i = 1; i <= table.seatCount; i++) {
        const seat = (hand.dealer + i) % table.seatCount;
        holdings[seat].push(...stock.splice(0, seat === taker ? 2 : 3));
    }
    return holdings;
}
export const belote = {
    meta: { id: 'belote', name: 'Belote', rotation: 'anticlockwise' },
    defaultConfig: DEFAULT_BELOTE_CONFIG,
    validateConfig: validateBeloteConfig,
    table: (_config) => PARTNERSHIP_4,
    tricksPerHand: (_config) => BELOTE_TRICKS_PER_HAND,
    deck: beloteDeck,
    dealPlan: beloteDealPlan,
    auction: beloteAuction,
    ranking(hand, _config) {
        return beloteRanking(hand.contract?.trump ?? null);
    },
    legalPlays(hand, seat, table, config) {
        return beloteLegalPlays(hand.secret.holdings[seat] ?? [], seat, beloteCurrentTrick(hand), beloteRanking(hand.contract?.trump ?? null), table, config);
    },
    initHandExtra: () => INIT_EXTRA,
    initMatchExtra: () => ({ litige: 0, lastHand: null }),
    onContract(hand, table, config) {
        return {
            extra: INIT_EXTRA,
            pending: null,
            activeSeats: Array.from({ length: table.seatCount }, (_, i) => i),
            holdings: config.variant === 'classic' ? completeClassicDeal(hand, table) : undefined,
            // Rules §1.2: the player to the dealer's right leads, whoever took.
            leader: (hand.dealer + 1) % table.seatCount,
        };
    },
    applyDecision() {
        return { ok: false, error: 'Belote has no decisions' };
    },
    /** Belote-Rebelote is announced as the cards go down (rules §5.1): "Belote"
     *  with the first of the trump King and Queen, if the same hand holds the
     *  other, and "Rebelote" with the second. */
    onCardPlayed(hand, seat, card, _nominate) {
        const trump = hand.contract?.trump;
        if (!trump || suitOf(card) !== trump)
            return hand.extra;
        const rank = rankOf(card);
        if (rank !== 12 && rank !== 13)
            return hand.extra;
        const other = `${rank === 12 ? 13 : 12}${trump}`;
        if (hand.secret.holdings[seat]?.includes(other))
            return { ...hand.extra, belote: { seat, stage: 'belote' } };
        if (hand.extra.belote?.seat === seat)
            return { ...hand.extra, belote: { seat, stage: 'rebelote' } };
        return hand.extra;
    },
    /** Declarations are settled once trick 1 is complete, and the side that
     *  wins the comparison lays its combinations face up (rules §5.5). */
    onTrickComplete(hand, config, table) {
        const trump = hand.contract?.trump;
        if (!trump || hand.extra.declarations !== null || beloteCurrentTrick(hand).index !== 0)
            return hand.extra;
        if (!config.declarations)
            return { ...hand.extra, declarations: [] };
        const result = resolveDeclarations(cardsDealt(hand.tricks, hand.secret.holdings), trump, hand.dealer, table);
        return { ...hand.extra, declarations: result.declarations, declarationsTeam: result.team };
    },
    nextLeader: (_hand, winner) => winner,
    scoreHand: beloteScoreHand,
    onPassedOut: () => ({ kind: 'redeal' }),
    matchEnd: (state, config) => beloteMatchEnd(state, config),
    roles(_hand, table) {
        const all = Array.from({ length: table.seatCount }, (_, i) => i);
        return { activeSeats: all, controllerOf: all };
    },
    visibility: beloteVisibility,
    contractLabel(contract) {
        return contract ? contract.label : 'Passed out';
    },
};
//# sourceMappingURL=module.js.map